import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6dt98itehdqnvnpj3v0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-12 sm:mb-16">
          <span className="inline-block text-xs font-display font-semibold uppercase tracking-[3px] text-sage-500 mb-2 sm:mb-3">
            Get In Touch
          </span>
          <h3 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-gray-900 mb-3 sm:mb-4 tracking-tight">
            Let&apos;s Create Something Amazing
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 max-w-2xl mx-auto px-4">
            Open for collaborations with NYC brands and beyond
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm p-6 sm:p-8 lg:p-12 border border-sage-100">
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-5 sm:space-y-6">
              <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
                <div>
                  <label htmlFor="name" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3.5 sm:px-4 py-2.5 sm:py-3 border border-sage-200 rounded-lg focus:ring-2 focus:ring-sage-300 focus:border-transparent transition-all text-sm bg-sage-50/50"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3.5 sm:px-4 py-2.5 sm:py-3 border border-sage-200 rounded-lg focus:ring-2 focus:ring-sage-300 focus:border-transparent transition-all text-sm bg-sage-50/50"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="company" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Company / Brand
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-3.5 sm:px-4 py-2.5 sm:py-3 border border-sage-200 rounded-lg focus:ring-2 focus:ring-sage-300 focus:border-transparent transition-all text-sm bg-sage-50/50"
                  placeholder="Your Brand Name"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Project Details *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-3.5 sm:px-4 py-2.5 sm:py-3 border border-sage-200 rounded-lg focus:ring-2 focus:ring-sage-300 focus:border-transparent transition-all resize-none text-sm bg-sage-50/50"
                  placeholder="Tell me about your project and collaboration ideas..."
                ></textarea>
                <p className="text-xs text-gray-400 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-sage-500 to-caramel-400 text-white py-3.5 sm:py-4 rounded-full font-display font-medium hover:from-sage-600 hover:to-caramel-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap shadow-md text-sm sm:text-base"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-xs sm:text-sm">
                  Thank you! Your message has been sent successfully. I&apos;ll get back to you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-xs sm:text-sm">
                  Oops! Something went wrong. Please try again.
                </div>
              )}
            </form>
          </div>

          <div className="mt-10 sm:mt-12 pt-10 sm:pt-12">
            <div className="grid md:grid-cols-3 gap-6 sm:gap-8 text-center">
              <div>
                <div className="w-11 sm:w-12 h-11 sm:h-12 flex items-center justify-center bg-sage-100 rounded-full mx-auto mb-3">
                  <i className="ri-mail-line text-sage-600 text-lg sm:text-xl"></i>
                </div>
                <h4 className="font-display font-medium text-gray-900 mb-1 text-sm sm:text-base">
                  <a href="mailto:alex@example.com" className="hover:text-sage-600 transition-colors">Email</a>
                </h4>
                <a href="mailto:alex@example.com" className="text-xs sm:text-sm text-gray-600 hover:text-sage-600 transition-colors">
                  alex@example.com
                </a>
              </div>

              <div>
                <div className="w-11 sm:w-12 h-11 sm:h-12 flex items-center justify-center bg-sage-100 rounded-full mx-auto mb-3">
                  <i className="ri-phone-line text-sage-600 text-lg sm:text-xl"></i>
                </div>
                <h4 className="font-display font-medium text-gray-900 mb-1 text-sm sm:text-base">Phone</h4>
                <a href="tel:+12125551234" className="text-xs sm:text-sm text-gray-600 hover:text-sage-600 transition-colors">
                  (212) 555-1234
                </a>
              </div>

              <div>
                <div className="w-11 sm:w-12 h-11 sm:h-12 flex items-center justify-center bg-sage-100 rounded-full mx-auto mb-3">
                  <i className="ri-map-pin-line text-sage-600 text-lg sm:text-xl"></i>
                </div>
                <h4 className="font-display font-medium text-gray-900 mb-1 text-sm sm:text-base">Location</h4>
                <p className="text-xs sm:text-sm text-gray-600">New York City, NY</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
